'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { subtitleWords, spokenWordFraction } from '../_pure/subtitleText.js';
export { subtitleWords, spokenWordFraction } from '../_pure/subtitleText.js';
const UNSPOKEN_OPACITY = 0.5;
function applyTrace(element, trace, fraction) {
    if (trace === 'brighten') {
        element.style.opacity = String(UNSPOKEN_OPACITY + (1 - UNSPOKEN_OPACITY) * fraction);
    }
    else {
        element.style.backgroundSize = `${fraction * 100}% 2px`;
    }
}
/**
 * The in-scene caption. With a trace, an animation-frame loop reads the spoken progress and styles each
 * word directly, so following the voice never re-renders the runtime.
 */
export function SubtitleBand({ text, trace, readProgress, bottom = '6%' }) {
    const words = useMemo(() => subtitleWords(text), [text]);
    const wordElements = useRef([]);
    useLayoutEffect(() => {
        if (!trace)
            return;
        const elements = wordElements.current.slice(0, words.length);
        let lastOffset = 0;
        let frame = 0;
        const render = () => {
            const progress = readProgress?.();
            // A finished or absent timeline has spoken the whole caption; another sentence keeps the last trace.
            if (!progress)
                lastOffset = text.length;
            else if (progress.text === text)
                lastOffset = progress.offset;
            words.forEach((word, index) => {
                const element = elements[index];
                if (element && typeof word !== 'string')
                    applyTrace(element, trace, spokenWordFraction(word, lastOffset));
            });
            frame = requestAnimationFrame(render);
        };
        render();
        return () => {
            cancelAnimationFrame(frame);
            for (const element of elements) {
                if (!element)
                    continue;
                element.style.opacity = '';
                element.style.backgroundSize = '';
            }
        };
    }, [text, trace, words, readProgress]);
    const wordStyle = trace === 'underline' ? {
        backgroundImage: 'linear-gradient(currentColor, currentColor)', backgroundRepeat: 'no-repeat',
        backgroundPosition: '0 100%', backgroundSize: '0% 2px', paddingBottom: 2,
    } : trace === 'brighten' ? { opacity: UNSPOKEN_OPACITY } : undefined;
    return (_jsx("div", { "aria-live": "polite", style: { position: 'absolute', left: '50%', bottom, transform: 'translateX(-50%)',
            maxWidth: 'min(72ch, 80%)', padding: '0.4em 0.9em', borderRadius: 6, background: 'rgba(0, 0, 0, 0.40)',
            color: '#fff', fontSize: 'clamp(15px, 1.6vw, 22px)', lineHeight: 1.35, textAlign: 'center',
            pointerEvents: 'none', zIndex: 9 }, children: trace ? words.map((word, index) => typeof word === 'string' ? word : (_jsx("span", { ref: (element) => { wordElements.current[index] = element; }, style: wordStyle, children: word.italic ? _jsx("em", { children: word.text }) : word.text }, `${text}:${index}`))) : words.map((word, index) => typeof word === 'string' ? word
            : word.italic ? _jsx("em", { children: word.text }, index) : word.text) }));
}
